// @dx hook
import { useList } from "@refinedev/core";
import { useMemo } from "react";
import type { Country } from "@shared/types/common";
export const useCountries = () => {
  const { query: { data, isLoading } } = useList<Country>({
    resource: "countries",
    pagination: { mode: "off" },
    sorters: [{ field: "name_pl", order: "asc" }],
    queryOptions: { staleTime: 10 * 60 * 1000 },
  });

  // Memoized so the derived option lists below (and consumers) keep a stable
  // reference between renders when the query data hasn't changed.
  const countries = useMemo(() => data?.data ?? [], [data?.data]);

  const getCountryName = (code: string) =>
    countries.find((c) => c.code === code)?.name_pl || code;

  const countryOptions = useMemo(() =>
    countries.map((c) => ({
      value: c.code,
      label: `${c.name_pl} (${c.code})`,
    })), [countries]);

  const euCountryOptions = useMemo(() =>
    countries
      .filter((c) => c.is_eu)
      .map((c) => ({
        value: c.code,
        label: `${c.name_pl} (${c.code})`,
      })), [countries]);

  const languageOptions = useMemo(() => {
    const seen = new Set<string>();
    return countries
      .filter((c) => c.language_code && !seen.has(c.language_code) && seen.add(c.language_code) as unknown as boolean)
      .map((c) => ({ value: c.language_code as string, label: (c.language_code as string).toUpperCase() }))
      .sort((a, b) => a.value.localeCompare(b.value));
  }, [countries]);

  return { countries, countryOptions, euCountryOptions, getCountryName, isLoading, languageOptions };
};